﻿import * as paths from '../utils/paths';
import { parseStyleRules, getStyleType, parse } from '../utils/styleParser';
import { parseStyleRules as parseWebglStyle } from '../utils/style/webglStyleParser';
import { parseStyleRules as parseCanvasStyle } from '../utils/style/canvasStyleParser';
import * as styleTypes from '../utils/style/styleTypes';

export default class StyleHandler {
    constructor(rootURL) {
        this.rootURL = rootURL;
        this.styles = {};
        this.systemStyles = {};
    }

    loadStyle(styleId, isSystem) {
        let stylePath = paths.STYLE_URL;

        styleId = styleId == null ? '' : '' + styleId;

        if (styleId == '') {
            styleId = 'sys_' + styleTypes.SYSTEM_STYLE_ALL;
        }

        if (styleId.indexOf('sys_') == 0) {
            isSystem = true;
        }

        if (isSystem) {
            stylePath = paths.SYSTEM_STYLE_URL;
        }

        return new Promise((resolve, reject) => {
            if (styleId == null) {
                resolve();
            }
            else if (this.styles[styleId] != null) {
                resolve();
            } else {
                $.ajax({
                    type: "GET",
                    url: this.rootURL + stylePath + styleId.replaceAll('sys_', ''),
                    async: true,
                    contentType: "application/json; charset=utf-8",
                    success: (result) => {
                        if (result != null) {
                            try {
                                if (isSystem) {
                                    let systemStyle = JSON.parse(result);
                                    this.styles[styleId] = systemStyle.Content;
                                    this.styles[systemStyle.Id] = systemStyle.Content;
                                    this.systemStyles[styleId] = systemStyle.Id;
                                } else {
                                    let styleDefinition = JSON.parse(result);
                                    this.styles[styleId] = styleDefinition;
                                }
                                resolve();
                            } catch (ex) {
                                resolve();
                            }
                        } else {
                            resolve();
                        }
                    },
                    error: (e) => {
                        resolve();
                    }
                });
            }
        });
    }

    applyStyle(styleId, style) {
        if (this.styles[styleId] && this.styles[styleId].Type == 'CLUSTER' && style.Type != 'CLUSTER') {
            this.styles[styleId].Content = style;
        } else {
            this.styles[styleId] = style;
        }
    }

    getCanvasStyleById(styleId, type) {
        return parseStyleRules(this.styles[styleId], type, true);
    }

    getStyleById(styleId, type) {
        if (styleId == null) {
            return {
                styleType: styleTypes.STYLE_TYPE_WEBGL,
                style: {
                    featureStyle: [],
                    labelStyle: null
                }
            }
        }

        return parseStyleRules(this.styles[styleId], type, false);
    }

    getCombinedStyleForRules(defaultStyleRules, hoverStyleRules, selectionStyleRules) {
        let isDefaultWebgl = getStyleType(defaultStyleRules) == styleTypes.STYLE_TYPE_WEBGL;
        let isHoverWebgl = getStyleType(hoverStyleRules) == styleTypes.STYLE_TYPE_WEBGL;
        let isSelectionWebgl = getStyleType(selectionStyleRules) == styleTypes.STYLE_TYPE_WEBGL;

        let forceCanvas = !(isDefaultWebgl && isHoverWebgl && isSelectionWebgl);

        let defaultStyle = parseStyleRules(defaultStyleRules, styleTypes.DEFAULT_STYLE_TYPE, forceCanvas);
        let hoverStyle = parseStyleRules(hoverStyleRules, styleTypes.HOVER_STYLE_TYPE, forceCanvas);
        let selectionStyle = parseStyleRules(selectionStyleRules, styleTypes.SELECT_STYLE_TYPE, forceCanvas);

        let result;

        if (forceCanvas) {
            result = JSON.parse(JSON.stringify(defaultStyle));

            if (defaultStyle.style.rules.length > 1) {
                for (let i = 0; i < defaultStyle.style.rules.length; i++) {
                    let rule = defaultStyle.style.rules[i];

                    let index = 0;

                    if (hoverStyle.style.rules.length > 1) {
                        index = i;
                    }
                    else {
                        index = 0;
                    }
                    if (hoverStyle.style.rules.length > 0) {
                        let hoverRule = JSON.parse(JSON.stringify(hoverStyle.style.rules[index]));
                        hoverRule.filter = JSON.parse(JSON.stringify(rule.filter));
                        hoverRule.classificationIdx = rule.classificationIdx;

                        if (hoverRule.filter.predicates && hoverRule.filter.predicates.length) {
                            let statePredicate = hoverRule.filter.predicates.find(predicate => predicate.propertyname == 'xstate');
                            if (statePredicate) {
                                statePredicate.literal = styleTypes.HOVER_STYLE_TYPE;
                            }
                        }

                        result.style.rules.push(hoverRule);
                    }

                    if (selectionStyle.style.rules.length > 1) {
                        index = i;
                    }
                    else {
                        index = 0;
                    }
                    if (selectionStyle.style.rules.length > 0) {
                        let selectRule = JSON.parse(JSON.stringify(selectionStyle.style.rules[index]));
                        selectRule.filter = JSON.parse(JSON.stringify(rule.filter));
                        selectRule.classificationIdx = rule.classificationIdx;

                        if (selectRule.filter.predicates && selectRule.filter.predicates.length) {
                            let statePredicate = selectRule.filter.predicates.find(predicate => predicate.propertyname == 'xstate');
                            if (statePredicate) {
                                statePredicate.literal = styleTypes.SELECT_STYLE_TYPE;
                            }
                        }

                        result.style.rules.push(selectRule);
                    }
                }
            } else {
                if (hoverStyle.style.rules.length > 0) {
                    hoverStyle.style.rules.forEach(style => {
                        result.style.rules.push(style)
                    });
                }

                if (selectionStyle.style.rules.length > 0) {
                    selectionStyle.style.rules.forEach(style => {
                        result.style.rules.push(style)
                    });
                }
            }
        } else {
            result = defaultStyle;

            if (hoverStyle.style.featureStyle.length > 0) {
                hoverStyle.style.featureStyle.forEach(style => {
                    result.style.featureStyle.push(style);
                });
            }

            if (selectionStyle.style.featureStyle.length > 0) {
                selectionStyle.style.featureStyle.forEach(style => {
                    result.style.featureStyle.push(style);
                });
            }
        }

        return result;
    }

    getCombinedStyle(defaultStyleId, hoverStyleId, selectionStyleId) {
        if (this.styles[defaultStyleId] != null && this.styles[defaultStyleId].Type != null) {
            if (this.styles[defaultStyleId].Type.trim() == 'HEATMAP') {
                return {
                    styleType: styleTypes.STYLE_TYPE_HEATMAP,
                    style: this.styles[defaultStyleId]
                };
            } else if (this.styles[defaultStyleId].Type.trim() == 'HOTSPOT') {
                return {
                    styleType: styleTypes.STYLE_TYPE_HOTSPOT,
                    style: this.styles[defaultStyleId]
                };
            } else if (this.styles[defaultStyleId].Type.trim() == 'TEMATIC') {
                return {
                    styleType: styleTypes.STYLE_TYPE_THEMATIC,
                    style: this.styles[defaultStyleId]
                };
            } else if (this.styles[defaultStyleId].Type.trim() == 'CUSTOM') {
                return {
                    styleType: this.styles[defaultStyleId].Type,
                    styleSubType: this.styles[defaultStyleId].SubType,
                    style: this.styles[defaultStyleId]
                };
            } else if (this.styles[defaultStyleId].Type.trim() == 'CLUSTER') {
                return {
                    styleType: styleTypes.STYLE_TYPE_CLUSTER,
                    style: this.getCombinedStyleForRules(this.styles[defaultStyleId].Content, this.styles[hoverStyleId], this.styles[selectionStyleId])
                };
            }
        }

        let isDefaultWebgl = getStyleType(this.styles[defaultStyleId]) == styleTypes.STYLE_TYPE_WEBGL;
        let isHoverWebgl = getStyleType(this.styles[hoverStyleId]) == styleTypes.STYLE_TYPE_WEBGL;
        let isSelectionWebgl = getStyleType(this.styles[selectionStyleId]) == styleTypes.STYLE_TYPE_WEBGL;

        let forceCanvas = !(isDefaultWebgl && isHoverWebgl && isSelectionWebgl);

        let defaultStyle = parseStyleRules(this.styles[defaultStyleId], styleTypes.DEFAULT_STYLE_TYPE, forceCanvas);
        let hoverStyle = parseStyleRules(this.styles[hoverStyleId], styleTypes.HOVER_STYLE_TYPE, forceCanvas);
        let selectionStyle = parseStyleRules(this.styles[selectionStyleId], styleTypes.SELECT_STYLE_TYPE, forceCanvas);

        let result;

        if (forceCanvas) {
            result = JSON.parse(JSON.stringify(defaultStyle));

            if (defaultStyle.style.rules.length > 1) {
                for (let i = 0; i < defaultStyle.style.rules.length; i++) {
                    let rule = defaultStyle.style.rules[i];

                    let index = 0;

                    if (hoverStyle.style.rules.length > i) {
                        index = i;
                    }
                    else {
                        index = 0;
                    }
                    if (hoverStyle.style.rules.length > 0) {
                        let hoverRule = JSON.parse(JSON.stringify(hoverStyle.style.rules[index]));
                        hoverRule.classificationIdx = rule.classificationIdx;

                        if (hoverRule.filter.predicates && hoverRule.filter.predicates.length) {
                            hoverRule.filter = JSON.parse(JSON.stringify(rule.filter));
                            let statePredicate = hoverRule.filter.predicates.find(predicate => predicate.propertyname == 'xstate');
                            if (statePredicate) {
                                statePredicate.literal = styleTypes.HOVER_STYLE_TYPE;
                            }
                        }

                        result.style.rules.push(hoverRule);
                    }

                    if (selectionStyle.style.rules.length > i) {
                        index = i;
                    }
                    else {
                        index = 0;
                    }
                    if (selectionStyle.style.rules.length > 0) {
                        let selectRule = JSON.parse(JSON.stringify(selectionStyle.style.rules[index]));
                        selectRule.classificationIdx = rule.classificationIdx;

                        if (selectRule.filter.predicates && selectRule.filter.predicates.length) {
                            selectRule.filter = JSON.parse(JSON.stringify(rule.filter));
                            let statePredicate = selectRule.filter.predicates.find(predicate => predicate.propertyname == 'xstate');
                            if (statePredicate) {
                                statePredicate.literal = styleTypes.SELECT_STYLE_TYPE;
                            }
                        }

                        result.style.rules.push(selectRule);
                    }
                }
            } else {
                if (hoverStyle.style.rules.length > 0) {
                    hoverStyle.style.rules.forEach(style => {
                        result.style.rules.push(style)
                    });
                }

                if (selectionStyle.style.rules.length > 0) {
                    selectionStyle.style.rules.forEach(style => {
                        result.style.rules.push(style)
                    });
                }
            }
        } else {
            result = defaultStyle;

            if (hoverStyle.style.featureStyle.length > 0) {
                hoverStyle.style.featureStyle.forEach(style => {
                    result.style.featureStyle.push(style);
                });
            }

            if (selectionStyle.style.featureStyle.length > 0) {
                selectionStyle.style.featureStyle.forEach(style => {
                    result.style.featureStyle.push(style);
                });
            }
        }

        return result;
    }

    getThematicCombinedStyle(defaultStyleId, hoverStyleId, selectionStyleId, min, max, strokeWidth) {
        let defaultStyleDescriptor = this._convertThematicToNormalStyle(defaultStyleId, min, max, strokeWidth);
        let result = this._combineThematicDescriptor(defaultStyleDescriptor, hoverStyleId, selectionStyleId);

        // intervalele de afisat in legenda, calculate per-layer (NU mutam descriptorul partajat)
        if (result != null) {
            result.classificationIntervals = this._buildThematicDisplayIntervals(defaultStyleId, min, max);
        }

        return result;
    }

    // clasificare pe praguri deja calculate (client peste feature-uri, sau server peste distributia DB)
    getThematicCombinedStyleFromBreaks(defaultStyleId, hoverStyleId, selectionStyleId, breaks, strokeWidth) {
        let defaultStyleDescriptor = this._convertThematicBreaksToNormalStyle(defaultStyleId, breaks, strokeWidth);
        let result = this._combineThematicDescriptor(defaultStyleDescriptor, hoverStyleId, selectionStyleId);

        if (result != null) {
            result.classificationIntervals = this._buildThematicDisplayIntervalsFromBreaks(defaultStyleId, breaks);
        }

        return result;
    }

    _combineThematicDescriptor(defaultStyleDescriptor, hoverStyleId, selectionStyleId) {
        let result;

        if (hoverStyleId == null) {
            let defaultStyle = parseCanvasStyle(defaultStyleDescriptor, styleTypes.DEFAULT_STYLE_TYPE);
            let hoverStyle = parseCanvasStyle(this.styles[hoverStyleId], styleTypes.HOVER_STYLE_TYPE);
            let selectionStyle = parseCanvasStyle(this.styles[selectionStyleId], styleTypes.SELECT_STYLE_TYPE);
            result = defaultStyle;

            if (hoverStyle.style.featureStyle != null && hoverStyle.style.featureStyle.length > 0) {
                hoverStyle.style.featureStyle.forEach(style => {
                    result.style.featureStyle.push(style);
                });
            }

            if (selectionStyle.style.featureStyle != null && selectionStyle.style.featureStyle.length > 0) {
                selectionStyle.style.featureStyle.forEach(style => {
                    result.style.featureStyle.push(style);
                });
            }
        } else {
            let defaultStyle = parseCanvasStyle(defaultStyleDescriptor, styleTypes.DEFAULT_STYLE_TYPE);
            let hoverStyle = parseCanvasStyle(this.styles[hoverStyleId], styleTypes.HOVER_STYLE_TYPE);
            let selectionStyle = parseCanvasStyle(this.styles[selectionStyleId], styleTypes.SELECT_STYLE_TYPE);

            result = defaultStyle;

            if (hoverStyle.style.rules.length > 0) {
                hoverStyle.style.rules.forEach(style => {
                    result.style.rules.push(style)
                });
            }

            if (selectionStyle.style.rules.length > 0) {
                selectionStyle.style.rules.forEach(style => {
                    result.style.rules.push(style)
                });
            }
        }

        return result;
    }

    // N clase = min(muchii-1, numar culori); pastram zecimalele (nu rotunjim ca la EQUAL)
    _convertThematicBreaksToNormalStyle(defaultStyleId, breaks, strokeWidth) {
        let thematic = this.styles[defaultStyleId];
        let rules = [];

        if (breaks == null || breaks.length < 2 || thematic.Intervals == null) {
            return rules;
        }

        let n = Math.min(breaks.length - 1, thematic.Intervals.length);

        for (let i = 0; i < n; i++) {
            let interval = thematic.Intervals[i];
            let bmin = breaks[i];
            let bmax = breaks[i + 1];

            // NU mai scriem inapoi in descriptor (partajat): pragurile ajung in legenda prin classificationIntervals
            rules.push(this._getThematicRule(thematic.NameEntityField, i == 0 ? (bmin - 1) : bmin, i == n - 1 ? (bmax + 1) : bmax, interval.color, thematic.Opacity, strokeWidth));
        }

        return rules;
    }

    // clasificare categoriala (valori unice): reguli de egalitate per valoare
    getThematicCategorizedStyle(defaultStyleId, hoverStyleId, selectionStyleId, categories, strokeWidth) {
        let defaultStyleDescriptor = this._convertThematicCategoriesToNormalStyle(defaultStyleId, categories, strokeWidth);
        let result = this._combineThematicDescriptor(defaultStyleDescriptor, hoverStyleId, selectionStyleId);

        if (result != null) {
            result.classificationIntervals = this._buildThematicCategorizedDisplayIntervals(defaultStyleId, categories);
        }

        return result;
    }

    _convertThematicCategoriesToNormalStyle(defaultStyleId, categories, strokeWidth) {
        let thematic = this.styles[defaultStyleId];

        // NU mutam cache-ul de stil (this.styles[id] e partajat intre layere): construim regulile
        // din categoriile primite (calculate la runtime) sau, in lipsa lor, din cele salvate
        let cats = categories != null ? categories : (thematic.Intervals || []);

        let rules = [];

        for (let i = 0; i < cats.length; i++) {
            let cat = cats[i];

            if (cat.value == null || cat.value === '') {
                continue;
            }

            rules.push(this._getThematicCategoryRule(thematic.NameEntityField, cat.value, cat.color, thematic.Opacity, strokeWidth));
        }

        return rules;
    }

    _convertThematicToNormalStyle(defaultStyleId, min, max, strokeWidth) {
        if (min == null || max == null) {
            let thematic = this.styles[defaultStyleId];
            let rules = [];

            for (let i = 0; i < thematic.Intervals.length; i++) {
                let interval = thematic.Intervals[i];
                // valori manuale salvate: folosim locale, fara sa mutam descriptorul partajat
                let cmin = this._customConvert(interval.min);
                let cmax = this._customConvert(interval.max);

                rules.push(this._getThematicRule(thematic.NameEntityField, i == 0 ? (cmin - 1) : cmin, i == thematic.Intervals.length - 1 ? (cmax + 1) : cmax, interval.color, thematic.Opacity, strokeWidth));
            }

            return rules;
        }
        else {
            let thematic = this.styles[defaultStyleId];

            let delta = (max - min) / thematic.Intervals.length;
            let start = 0;

            let rules = [];

            for (let i = 0; i < thematic.Intervals.length; i++) {
                let interval = thematic.Intervals[i];
                let intervalMin = min + start;
                let intervalMax = intervalMin + delta;
                start += delta;

                let cmin = this._customConvert(intervalMin);
                let cmax = this._customConvert(intervalMax);

                rules.push(this._getThematicRule(thematic.NameEntityField, i == 0 ? (cmin - 1) : cmin, i == thematic.Intervals.length - 1 ? (cmax + 1) : cmax, interval.color, thematic.Opacity, strokeWidth));
            }

            return rules;
        }
    }

    _customConvert(number) {
        if (number >= 1) {
            return Math.round(number);
        } else {
            return Math.floor(number);
        }
    }

    // intervalele afisate in legenda pentru clasificarea graduala pe praguri (cuantile/jenks/stddev/manual pe breaks)
    _buildThematicDisplayIntervalsFromBreaks(defaultStyleId, breaks) {
        let thematic = this.styles[defaultStyleId];
        let intervals = [];

        if (breaks == null || breaks.length < 2 || thematic == null || thematic.Intervals == null) {
            return intervals;
        }

        let n = Math.min(breaks.length - 1, thematic.Intervals.length);

        for (let i = 0; i < n; i++) {
            intervals.push({ min: breaks[i], max: breaks[i + 1], color: thematic.Intervals[i].color });
        }

        return intervals;
    }

    // intervalele afisate in legenda pentru EQUAL (min/max => impartire egala) sau valori manuale (min/max == null)
    _buildThematicDisplayIntervals(defaultStyleId, min, max) {
        let thematic = this.styles[defaultStyleId];
        let intervals = [];

        if (thematic == null || thematic.Intervals == null) {
            return intervals;
        }

        if (min == null || max == null) {
            for (let i = 0; i < thematic.Intervals.length; i++) {
                let interval = thematic.Intervals[i];
                // pastram capetele deschise (min/max null la prima/ultima clasa) => legenda le afiseaza ca ≤/≥
                intervals.push({
                    min: interval.min != null ? this._customConvert(interval.min) : null,
                    max: interval.max != null ? this._customConvert(interval.max) : null,
                    color: interval.color
                });
            }

            return intervals;
        }

        let delta = (max - min) / thematic.Intervals.length;
        let start = 0;

        for (let i = 0; i < thematic.Intervals.length; i++) {
            let intervalMin = min + start;
            let intervalMax = intervalMin + delta;
            start += delta;

            intervals.push({ min: this._customConvert(intervalMin), max: this._customConvert(intervalMax), color: thematic.Intervals[i].color });
        }

        return intervals;
    }

    // intervalele afisate in legenda pentru clasificarea categoriala (valoare + eticheta + culoare)
    _buildThematicCategorizedDisplayIntervals(defaultStyleId, categories) {
        let thematic = this.styles[defaultStyleId];
        let cats = categories != null ? categories : (thematic != null ? (thematic.Intervals || []) : []);
        let intervals = [];

        for (let i = 0; i < cats.length; i++) {
            let cat = cats[i];

            if (cat.value == null || cat.value === '') {
                continue;
            }

            intervals.push({ value: cat.value, label: (cat.label != null && cat.label !== '') ? cat.label : cat.value, color: cat.color });
        }

        return intervals;
    }

    _getThematicRule(field, min, max, color, opacity, strokeWidth) {
        let result = {
            "$type": "Sld.Rule, XPortal4.Core",
            "Name": "DefaultAllStyle",
            "Title": "",
            "MinScaleDenominatorSpecified": false,
            "MaxScaleDenominatorSpecified": false,
            "Items": [
                {
                    "$type": "Sld.RuleSymbolizerPolygonSymbolizer, XPortal4.Core",
                    "Fill": {
                        "styling": {
                            "fill": color,
                            "fillOpacity": opacity
                        }
                    },
                    "Stroke": {
                        "styling": {
                            "stroke": color,
                            "strokeDasharray": null,
                            "strokeLinecap": "butt",
                            "strokeLinejoin": "miter",
                            "strokeOpacity": 1,
                            "strokeWidth": (strokeWidth == null || strokeWidth == '' ? 2 : strokeWidth)
                        }
                    },
                    "Id": 3,
                    "NodeText": "Polygon Theme",
                    "index": 2,
                    "_matchFilter": true
                }
            ],
            "Id": 0,
            "NodeText": "DefaultAllStyle",
            "index": 0,
            "expanded": true,
            "selected": true,
            "ValidError": null,
            "Item": {
                "AND": [
                    {
                        ">=": [
                            field,
                            min
                        ]
                    },
                    {
                        "<": [
                            field,
                            max
                        ]
                    }
                ]
            },
            "MinScaleDenominator": null,
            "MaxScaleDenominator": null,
            "_matchFilter": true
        }

        return result;
    }

    // regula categoriala: potrivire pe egalitate (field = value); parserul suporta '=' (string sau numeric)
    _getThematicCategoryRule(field, value, color, opacity, strokeWidth) {
        let result = {
            "$type": "Sld.Rule, XPortal4.Core",
            "Name": "DefaultAllStyle",
            "Title": "",
            "MinScaleDenominatorSpecified": false,
            "MaxScaleDenominatorSpecified": false,
            "Items": [
                {
                    "$type": "Sld.RuleSymbolizerPolygonSymbolizer, XPortal4.Core",
                    "Fill": {
                        "styling": {
                            "fill": color,
                            "fillOpacity": opacity
                        }
                    },
                    "Stroke": {
                        "styling": {
                            "stroke": color,
                            "strokeDasharray": null,
                            "strokeLinecap": "butt",
                            "strokeLinejoin": "miter",
                            "strokeOpacity": 1,
                            "strokeWidth": (strokeWidth == null || strokeWidth == '' ? 2 : strokeWidth)
                        }
                    },
                    "Id": 3,
                    "NodeText": "Polygon Theme",
                    "index": 2,
                    "_matchFilter": true
                }
            ],
            "Id": 0,
            "NodeText": "DefaultAllStyle",
            "index": 0,
            "expanded": true,
            "selected": true,
            "ValidError": null,
            "Item": {
                "=": [
                    field,
                    value
                ]
            },
            "MinScaleDenominator": null,
            "MaxScaleDenominator": null,
            "_matchFilter": true
        }

        return result;
    }
}